/**
 * Step 4 do wizard /imports — confirmação e apply (TH-53213).
 *
 * Carrega:
 *  - GET /api/imports/{batchId} → resumo (mesma chave do Step 3, vem do cache).
 *  - POST /api/imports/{batchId}/apply → efetiva o batch.
 *
 * Decisões:
 *  - Reutiliza a queryKey ["import-batch", batchId] do Step 3 → render imediato
 *    sem refetch quando o usuário avança.
 *  - Defesa em profundidade: se `error_rows > 0` ou `status !== "preview"`
 *    quando este componente renderiza, mostra alerta e bloqueia o botão. Step 3
 *    já não permite avançar nesse caso, mas a tela não confia nisso sozinha.
 *  - Schema do ImportApplyResult (apps/api/.../schemas/import_batch.py) só
 *    devolve {id, status, applied_lines, skipped_error_lines, applied_at}.
 *    Nada de products_created/updated detalhado — não inventar.
 *  - Estados: idle | applying | done | error. `done` substitui o card por uma
 *    tela de sucesso; `error` mantém o card e mostra detail + retry.
 */

import { useMutation, useQuery } from "@tanstack/react-query";

import { Button } from "@/components/ui/Button";

interface ImportPreview {
  id: string;
  status: string;
  store_id: string | null;
  mapping_id: string | null;
  file_url: string;
  total_rows: number;
  ok_rows: number;
  error_rows: number;
  warning_rows: number;
  created_at: string;
}

interface ImportApplyResult {
  id: string;
  status: string;
  applied_lines: number;
  skipped_error_lines: number;
  applied_at: string | null;
}

async function fetchPreview(batchId: string): Promise<ImportPreview> {
  const res = await fetch(`/api/imports/${batchId}`, {
    credentials: "include",
  });
  if (!res.ok) {
    throw new Error(`HTTP ${res.status}`);
  }
  return (await res.json()) as ImportPreview;
}

async function applyBatch(batchId: string): Promise<ImportApplyResult> {
  const res = await fetch(`/api/imports/${batchId}/apply`, {
    method: "POST",
    credentials: "include",
  });
  if (!res.ok) {
    let detail = `HTTP ${res.status}`;
    try {
      const body = (await res.json()) as { detail?: string };
      if (body.detail) detail = body.detail;
    } catch {
      // não-json — mantém HTTP <status>
    }
    throw new Error(detail);
  }
  return (await res.json()) as ImportApplyResult;
}

/** Extrai o nome de arquivo do file_url (último segmento do path). */
function fileNameFromUrl(url: string): string {
  try {
    const path = new URL(url).pathname;
    const segs = path.split("/").filter(Boolean);
    return segs[segs.length - 1] ?? url;
  } catch {
    const segs = url.split("/").filter(Boolean);
    return segs[segs.length - 1] ?? url;
  }
}

export interface ImportsStep4ConfirmProps {
  batchId: string;
  storeName?: string;
  mappingName?: string;
  onBack: () => void;
  onDone: () => void;
}

export function ImportsStep4Confirm({
  batchId,
  storeName,
  mappingName,
  onBack,
  onDone,
}: ImportsStep4ConfirmProps) {
  const previewQuery = useQuery({
    queryKey: ["import-batch", batchId],
    queryFn: () => fetchPreview(batchId),
  });

  const applyMutation = useMutation<ImportApplyResult, Error, void>({
    mutationFn: () => applyBatch(batchId),
  });

  const summary = previewQuery.data;
  const blockingError =
    summary !== undefined &&
    (summary.status !== "preview" || summary.error_rows > 0);

  // Estado terminal: applied com sucesso → tela dedicada.
  if (applyMutation.isSuccess && applyMutation.data) {
    return (
      <ApplySuccess result={applyMutation.data} onDone={onDone} />
    );
  }

  return (
    <div className="flex flex-col gap-5">
      <div className="text-[10px] font-semibold uppercase tracking-[0.1em] text-[var(--color-muted-foreground)]">
        Passo 4 de 4 · Confirmar e aplicar
      </div>

      {previewQuery.isLoading ? (
        <p className="text-sm text-[var(--color-muted-foreground)]">
          carregando resumo…
        </p>
      ) : previewQuery.error ? (
        <div
          role="alert"
          className="rounded-md border border-[var(--color-destructive)]/40 bg-[var(--color-destructive)]/10 p-3 text-xs text-[var(--color-destructive)]"
        >
          erro ao carregar resumo do batch
        </div>
      ) : summary ? (
        <>
          <SummaryCard
            summary={summary}
            storeName={storeName}
            mappingName={mappingName}
          />

          {blockingError ? (
            <div
              role="alert"
              className="rounded-md border border-[var(--color-destructive)]/40 bg-[var(--color-destructive)]/10 p-3 text-xs text-[var(--color-destructive)]"
            >
              {summary.error_rows > 0
                ? `Existem ${summary.error_rows} linha${
                    summary.error_rows === 1 ? "" : "s"
                  } inválida${
                    summary.error_rows === 1 ? "" : "s"
                  } — volte ao passo 3 e corrija antes de aplicar.`
                : `Batch em status "${summary.status}" — só "preview" pode ser aplicado.`}
            </div>
          ) : (
            <div className="rounded-md border border-[var(--color-border)] bg-[var(--color-card)] p-4 text-sm">
              <p className="text-[var(--color-foreground)]">
                Aplicar criará/atualizará{" "}
                <span className="font-mono font-semibold tabular-nums">
                  {summary.ok_rows + summary.warning_rows}
                </span>{" "}
                linha
                {summary.ok_rows + summary.warning_rows === 1 ? "" : "s"} no
                catálogo desta loja.
              </p>
              <p className="mt-2 text-xs text-[var(--color-muted-foreground)]">
                Esta ação registra preços novos e fecha os anteriores. É
                possível desfazer em até 24h após a aplicação.
              </p>
            </div>
          )}
        </>
      ) : null}

      {applyMutation.isError ? (
        <div
          role="alert"
          className="flex flex-col gap-2 rounded-md border border-[var(--color-destructive)]/40 bg-[var(--color-destructive)]/10 p-3 text-xs text-[var(--color-destructive)]"
        >
          <span>
            falha ao aplicar:{" "}
            {applyMutation.error?.message ?? "erro desconhecido"}
          </span>
          <div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => applyMutation.mutate()}
              disabled={applyMutation.isPending}
            >
              Tentar novamente
            </Button>
          </div>
        </div>
      ) : null}

      <div className="flex justify-between gap-2 pt-2">
        <Button
          type="button"
          variant="outline"
          onClick={onBack}
          disabled={applyMutation.isPending}
        >
          ← Voltar
        </Button>
        <Button
          type="button"
          onClick={() => applyMutation.mutate()}
          disabled={
            !summary ||
            blockingError ||
            applyMutation.isPending ||
            previewQuery.isLoading
          }
        >
          {applyMutation.isPending ? "Aplicando…" : "Aplicar importação"}
        </Button>
      </div>
    </div>
  );
}

interface SummaryCardProps {
  summary: ImportPreview;
  storeName?: string;
  mappingName?: string;
}

function SummaryCard({ summary, storeName, mappingName }: SummaryCardProps) {
  return (
    <div className="rounded-md border border-[var(--color-border)] bg-[var(--color-card)] p-4">
      <h3 className="text-[10px] font-semibold uppercase tracking-[0.1em] text-[var(--color-muted-foreground)]">
        Resumo da importação
      </h3>
      <dl className="mt-3 grid grid-cols-1 gap-2 text-xs sm:grid-cols-2">
        <SummaryRow label="Loja" value={storeName ?? "—"} />
        <SummaryRow label="Mapeamento" value={mappingName ?? "—"} />
        <SummaryRow
          label="Arquivo"
          value={fileNameFromUrl(summary.file_url)}
          mono
        />
        <SummaryRow
          label="Total de linhas"
          value={String(summary.total_rows)}
          mono
        />
        <SummaryRow
          label="Linhas válidas"
          value={String(summary.ok_rows)}
          mono
          tone="success"
        />
        <SummaryRow
          label="Avisos"
          value={String(summary.warning_rows)}
          mono
          tone={summary.warning_rows > 0 ? "warn" : undefined}
        />
        <SummaryRow
          label="Linhas inválidas"
          value={String(summary.error_rows)}
          mono
          tone={summary.error_rows > 0 ? "destructive" : undefined}
        />
        <SummaryRow label="Status" value={summary.status} />
      </dl>
    </div>
  );
}

interface SummaryRowProps {
  label: string;
  value: string;
  mono?: boolean;
  tone?: "success" | "warn" | "destructive";
}

function SummaryRow({ label, value, mono, tone }: SummaryRowProps) {
  const toneClass =
    tone === "success"
      ? "text-[var(--color-success)]"
      : tone === "warn"
        ? "text-[var(--color-warning)]"
        : tone === "destructive"
          ? "text-[var(--color-destructive)]"
          : "text-[var(--color-foreground)]";
  return (
    <div className="flex items-baseline justify-between gap-3 border-b border-[var(--color-border)] py-1.5 last:border-b-0 sm:border-b-0 sm:py-0.5">
      <dt className="text-[var(--color-muted-foreground)]">{label}</dt>
      <dd
        className={[
          "truncate text-right",
          mono ? "font-mono tabular-nums" : "",
          toneClass,
        ].join(" ")}
        title={value}
      >
        {value}
      </dd>
    </div>
  );
}

interface ApplySuccessProps {
  result: ImportApplyResult;
  onDone: () => void;
}

function ApplySuccess({ result, onDone }: ApplySuccessProps) {
  return (
    <div className="flex flex-col gap-5">
      <div className="text-[10px] font-semibold uppercase tracking-[0.1em] text-[var(--color-muted-foreground)]">
        Passo 4 de 4 · Concluído
      </div>

      <div className="rounded-md border border-[var(--color-success)]/40 bg-[var(--color-success)]/10 p-6 text-center">
        <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-full bg-[var(--color-success)]/20 text-2xl text-[var(--color-success)]">
          ✓
        </div>
        <h3 className="mt-3 text-base font-semibold text-[var(--color-foreground)]">
          Importação concluída
        </h3>
        <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">
          <span className="font-mono font-semibold tabular-nums text-[var(--color-foreground)]">
            {result.applied_lines}
          </span>{" "}
          linha{result.applied_lines === 1 ? "" : "s"} aplicada
          {result.applied_lines === 1 ? "" : "s"} ao catálogo.
          {result.skipped_error_lines > 0 ? (
            <>
              {" "}
              <span className="font-mono tabular-nums">
                {result.skipped_error_lines}
              </span>{" "}
              ignorada
              {result.skipped_error_lines === 1 ? "" : "s"} por erro.
            </>
          ) : null}
        </p>
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <Button type="button" onClick={onDone}>
          Voltar para o início
        </Button>
      </div>
    </div>
  );
}
